import { notNull, isNullish } from "@/utils/helpers";
import { logger } from "@/utils/logging/browser";

export type RealtimePath = "speech_to_text" | "tts" | "video_description";

export type RealtimeProvider =
  | "assemblyai"
  | "cartesia"
  | "elevenlabs"
  | "gemini"
  | "openai";

// Application-initiated closes never count as failures.
export type RealtimeCloseRequest = "destroy" | "commit" | "tts_idle";

export type RealtimeCloseDetails = {
  code: number | null;
  reason: string | null;
  wasClean: boolean | null;
};

export const getCloseEventDetails = (event: {
  code: number;
  reason: string;
  wasClean: boolean;
}): RealtimeCloseDetails => ({
  code: event.code,
  reason: event.reason || null,
  wasClean: event.wasClean,
});

export type RealtimeDisruption = {
  path: RealtimePath;
  provider: RealtimeProvider;
  kind: "connect_failed" | "unexpected_close";
  closeCode: number | null;
  wasClean: boolean | null;
  connectedDurationMs: number | null;
  attempts: number;
};

type RealtimeDisruptionListener = (disruption: RealtimeDisruption) => void;

const disruptionListeners = new Set<RealtimeDisruptionListener>();

// Clients live deep in the recorder engine and TTS store, so they publish here.
export const subscribeToRealtimeDisruptions = (
  listener: RealtimeDisruptionListener
): (() => void) => {
  disruptionListeners.add(listener);
  return () => {
    disruptionListeners.delete(listener);
  };
};

const notifyDisruption = (disruption: RealtimeDisruption) =>
  disruptionListeners.forEach((listener) => listener(disruption));

export type RealtimeConnectionLog = {
  recordAttempt: () => number;
  recordConnected: () => void;
  recordClosed: ({
    attempt,
    details,
  }: {
    attempt: number;
    details: RealtimeCloseDetails;
  }) => void;
  recordCloseRequested: (closeRequest: RealtimeCloseRequest) => void;
  recordConnectFailed: () => void;
};

// Emits one event per lifecycle: a connect call, its retries, and the close.
export const createRealtimeConnectionLog = ({
  path,
  provider,
}: {
  path: RealtimePath;
  provider: RealtimeProvider;
}): RealtimeConnectionLog => {
  const startedAt = performance.now();
  let attempts = 0;
  let connectedAt: number | null = null;
  let lastAttemptClose: RealtimeCloseDetails | null = null;
  let isSettled = false;

  const getConnectedDurationMs = () =>
    notNull(connectedAt) ? Math.round(performance.now() - connectedAt) : null;

  return {
    recordAttempt: () => {
      attempts += 1;
      lastAttemptClose = null;
      return attempts;
    },

    recordConnected: () => {
      connectedAt ??= performance.now();
    },

    // A superseded attempt's socket can finish closing after a retry connects.
    recordClosed: ({ attempt, details }) => {
      if (isSettled || attempt !== attempts) return;
      if (isNullish(connectedAt)) {
        lastAttemptClose = details;
        return;
      }
      isSettled = true;
      const connectedDurationMs = getConnectedDurationMs();
      logger.warn("interview.realtime.closed", {
        event: "interview.realtime.closed",
        path,
        provider,
        initiator: "unexpected",
        closeRequest: null,
        wasConnected: true,
        closeCode: details.code,
        wasClean: details.wasClean,
        connectedDurationMs,
        attempts,
      });
      notifyDisruption({
        path,
        provider,
        kind: "unexpected_close",
        closeCode: details.code,
        wasClean: details.wasClean,
        connectedDurationMs,
        attempts,
      });
    },

    // AssemblyAI drops its listeners on close(), so log at request time.
    recordCloseRequested: (closeRequest: RealtimeCloseRequest) => {
      if (isSettled) return;
      isSettled = true;
      logger.info("interview.realtime.closed", {
        event: "interview.realtime.closed",
        path,
        provider,
        initiator: "application",
        closeRequest,
        wasConnected: notNull(connectedAt),
        closeCode: null,
        wasClean: null,
        connectedDurationMs: getConnectedDurationMs(),
        attempts,
      });
    },

    recordConnectFailed: () => {
      if (isSettled) return;
      isSettled = true;
      logger.warn("interview.realtime.connect_failed", {
        event: "interview.realtime.connect_failed",
        path,
        provider,
        attempts,
        durationMs: Math.round(performance.now() - startedAt),
        closeCode: lastAttemptClose?.code ?? null,
        wasClean: lastAttemptClose?.wasClean ?? null,
      });
      notifyDisruption({
        path,
        provider,
        kind: "connect_failed",
        closeCode: lastAttemptClose?.code ?? null,
        wasClean: lastAttemptClose?.wasClean ?? null,
        connectedDurationMs: null,
        attempts,
      });
    },
  };
};
